/**
 * Dates and times as the panel shows them.  Ported from admin-live.js with
 * the rules that earned each one.
 *
 * Two kinds of value, never mixed up:
 *  - a WALL-CLOCK date or time at the mosque ('2026-10-20', '2026-09-21T18:00:00'):
 *    split into parts, never `new Date(iso)` — that is UTC midnight, which in
 *    Auckland is the next day (the trap that failed the fixtures twice);
 *  - an INSTANT (created_at, sentAt …): shown in Oslo time, whatever the
 *    office computer's own zone is (fault: "12:38" for 14:38).
 */
const MONTH_LONG = ['januar', 'februar', 'mars', 'april', 'mai', 'juni', 'juli', 'august', 'september', 'oktober', 'november', 'desember'];
const MONTH = ['jan', 'feb', 'mar', 'apr', 'mai', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'des'];
const WEEKDAY = ['sø', 'ma', 'ti', 'on', 'to', 'fr', 'lø'];
export const WEEKDAY_LONG = ['søndag', 'mandag', 'tirsdag', 'onsdag', 'torsdag', 'fredag', 'lørdag'];

/** '2026-10-20' → '20. oktober 2026'. */
export function longDate(iso: unknown): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso ?? ''));
  return m ? `${Number(m[3])}. ${MONTH_LONG[Number(m[2]) - 1]} ${m[1]}` : '';
}

/** '2026-09-09' → 'on 9. sep' (weekday from local wall-clock parts). */
export function shortDate(iso: string): string {
  const [y, m, d] = String(iso).split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  return WEEKDAY[dt.getDay()] + ' ' + d + '. ' + MONTH[m - 1];
}

/** An instant, in Oslo wall time: '19. sep. 2026, 14:38'. */
export function osloTime(iso: string | null | undefined): string {
  return iso ? new Date(iso).toLocaleString('nb-NO', { timeZone: 'Europe/Oslo', day: 'numeric', month: 'short',
    year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';
}

/** A wall-clock event time '2026-09-21T18:00:00' → '2026-09-21 18:00' (split, never parsed). */
export function wallClock(s: string | null | undefined): string {
  return String(s || '').replace('T', ' ').slice(0, 16);
}

/** Today at the mosque's desk, as YYYY-MM-DD from local parts. */
export function isoToday(now = new Date()): string {
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
}

/** The top-bar date, from the SERVER's day so the panel and its data agree. */
export function topDate(iso: string | null | undefined): string {
  const d = iso ? new Date(String(iso).length === 10 ? iso + 'T12:00:00' : iso) : new Date();
  if (isNaN(d.getTime())) return '';
  const s = d.toLocaleDateString('nb-NO', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export const kb = (n: number): string => n >= 1048576 ? (n / 1048576).toFixed(1).replace('.', ',') + ' MB'
  : Math.max(1, Math.round(n / 1024)) + ' kB';

export const plural = (n: number, one: string, many: string): string => `${n} ${n === 1 ? one : many}`;

/**
 * The mosque's followers at the platform, as the server shows them (FOLLOWING.md): today's count, or «færre enn 5»
 * below five — never a live figure, never who. null when following is off (the server sends no figure).
 */
export function followersText(f: unknown): string | null {
  if (!f || typeof f !== 'object') return null;
  const o = f as { count?: unknown; fewerThan?: unknown };
  if (typeof o.count === 'number') return plural(o.count, 'følger', 'følgere');
  if (typeof o.fewerThan === 'number') return `færre enn ${o.fewerThan} følgere`;
  return null;
}

/**
 * «Når N telefoner» (smart notifications, 2026-11-01): how many phones can get a notification from this mosque now, from
 * GET /push/audience (`devices`, the mosque's own phones; `followers`, today's count at the platform). With none at all,
 * the hint that says why — so staff see 0 BEFORE they send. Null when the answer has no count.
 */
export function phoneReachText(a: { devices?: unknown; followers?: unknown }): { text: string; hint: string | null } | null {
  if (typeof a.devices !== 'number') return null;
  const n = a.devices;
  const f = followersText(a.followers);
  return { text: `Når ${n.toLocaleString('nb-NO')} ${n === 1 ? 'telefon' : 'telefoner'}${f ? ` + ${f}` : ''}`,
           hint: n === 0 && !f ? 'Ingen telefoner er registrert ennå — appen må ha varsler på.' : null };
}

/* ---------------- Oslo dates for instants (QA 2026-10-16, C) ----------------
 * `String(createdAt).slice(0, 10)` is the UTC date: a request made at 00:30 in Oslo was listed as the day before, and
 * `new Date().toISOString().slice(0, 7)` opened the decision review on last month for the first hour or two of a month.
 * These read an instant on the mosque's wall (Europe/Oslo), whatever the office computer's own zone is. */
const ZONE = 'Europe/Oslo';
const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

/** The wall-clock parts of an instant in Oslo. */
function osloParts(at: Date): { y: number; m: number; d: number; h: number; min: number } {
  const o: Record<string, string> = {};
  for (const p of new Intl.DateTimeFormat('en-US', { timeZone: ZONE, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit' }).formatToParts(at)) o[p.type] = p.value;
  return { y: Number(o.year), m: Number(o.month), d: Number(o.day), h: Number(o.hour) % 24, min: Number(o.minute) };
}
const pad = (n: number) => String(n).padStart(2, '0');

/** An instant → its Oslo calendar date 'YYYY-MM-DD'. A date that is already only a date is returned as it is. '' when unreadable. */
export function osloDate(v: string | Date | null | undefined): string {
  if (v === null || v === undefined || v === '') return '';
  if (typeof v === 'string' && DATE_ONLY.test(v)) return v;
  const at = v instanceof Date ? v : new Date(v);
  if (isNaN(at.getTime())) return '';
  const p = osloParts(at);
  return `${p.y}-${pad(p.m)}-${pad(p.d)}`;
}

/** An instant (or a date) → the Oslo date as people write it: '23.10.2026' (Masjid UX redesign: dates in words people read). */
export function osloDay(v: string | Date | null | undefined): string {
  const d = osloDate(v);
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(d);
  return m ? `${m[3]}.${m[2]}.${m[1]}` : d;
}

/** The Oslo month 'YYYY-MM' of an instant (default: now) — for a month picker's starting value. */
export const osloMonth = (now: Date = new Date()): string => osloDate(now).slice(0, 7);

/** Now (or `now`) as an Oslo wall-clock 'YYYY-MM-DDTHH:MM', the value a datetime-local field takes. */
export function osloWallNow(now: Date = new Date()): string {
  const p = osloParts(now);
  return `${p.y}-${pad(p.m)}-${pad(p.d)}T${pad(p.h)}:${pad(p.min)}`;
}

/**
 * A datetime-local value ('2026-10-25T02:30'), read on the OSLO wall, → the ISO instant. Not `new Date(v)`, which reads
 * it on the office computer's wall. In the hour a clock skips or repeats it resolves to one fixed instant (the same
 * rule as core banners.instantOf). null when empty or unreadable — a caller says so instead of throwing (the withdrawal
 * form failed silently on `new Date('').toISOString()`).
 */
export function osloWallToIso(v: string | null | undefined): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(String(v || ''));
  if (!m) return null;
  const asUtc = Date.UTC(+m[1], +m[2] - 1, +m[3], +m[4], +m[5], +(m[6] || 0));
  const back = new Date(asUtc);
  if (isNaN(asUtc) || back.getUTCMonth() !== +m[2] - 1 || back.getUTCDate() !== +m[3] || back.getUTCHours() !== +m[4]) return null;
  const offset = (t: number) => { const p = osloParts(new Date(t)); return Date.UTC(p.y, p.m - 1, p.d, p.h, p.min) - Math.floor(t / 60000) * 60000; };
  const guess = asUtc - offset(asUtc);
  return new Date(asUtc - offset(guess)).toISOString();
}

/** A name in the genitive, as Norwegian writes it: «Saras», but «Idris'», «Max'», «Aziz'» (QA review P3). */
export const genitive = (name: string): string => (/[sxz]$/i.test(String(name).trim()) ? `${String(name).trim()}'` : `${String(name).trim()}s`);

/** An amount in kroner as Norwegians write it: 99.5 → '99,50 kr', 1200 → '1 200 kr'. '—' when there is none. */
export function kr(n: number | string | null | undefined): string {
  if (n === null || n === undefined || n === '') return '—';
  const v = Number(n);
  if (!isFinite(v)) return '—';
  const whole = Number.isInteger(v);
  // nb-NO groups thousands with a no-break space; a plain space reads the same and copies cleanly into a spreadsheet.
  return new Intl.NumberFormat('nb-NO', { minimumFractionDigits: whole ? 0 : 2, maximumFractionDigits: 2 }).format(v).replace(/\s/g, ' ') + ' kr';
}

/** «Inviter en ressursperson» (2026-11-01): which channels the platform can send now, as the server says. */
export type InviteChannels = { sms: boolean; email: boolean };
/** The channel a new invitation starts on: SMS when it can go, then e-mail, then a link to copy. */
export function defaultInviteChannel(ch: InviteChannels): string { return ch.sms ? 'sms' : ch.email ? 'email' : 'link'; }
/** Why a channel cannot be used now (the platform has no sender for it), or null. */
export function channelBlocked(channel: string, ch: InviteChannels): string | null {
  if (channel === 'sms' && !ch.sms) return 'SMS er ikke satt opp. Velg «Lenke jeg sender selv» og send lenken selv.';
  if (channel === 'email' && !ch.email) return 'E-post er ikke satt opp. Velg «Lenke jeg sender selv» og send lenken selv.';
  return null;
}
